import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function ClientVerifyPage() {
  const [searchParams] = useSearchParams();
  const { verifyMagicLink } = useClientAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setError(t('portal.verify.noToken'));
      return;
    }

    verifyMagicLink(token, i18n.language)
      .then(() => {
        setStatus('success');
        setTimeout(() => navigate('/portal/dashboard'), 1500);
      })
      .catch(err => {
        setStatus('error');
        setError(err instanceof Error ? err.message : t('portal.verify.invalidLink'));
      });
  }, [searchParams, verifyMagicLink, navigate, t, i18n.language]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-teal-500 animate-spin mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('portal.verify.verifying')}</h2>
            <p className="text-slate-600">{t('portal.verify.checkingLink')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-teal-100 flex items-center justify-center mb-6">
              <CheckCircle className="text-teal-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('portal.verify.welcome')}</h2>
            <p className="text-slate-600">{t('portal.verify.verified')}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-6">
              <XCircle className="text-rose-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('portal.verify.errorTitle')}</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/portal/login')}
              className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors"
            >
              {t('portal.verify.goToLogin')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
